<template>
  <section class="rounded-2xl border border-gray-200 p-4 space-y-4" :class="cardClass">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold text-[#0e141b]">Select Token to Swap</h3>
      <span v-if="sourceWalletAddress && tokensWithBalanceCount > 0" class="text-xs text-[#4e7397]">
        {{ tokensWithBalanceCount }} token{{ tokensWithBalanceCount === 1 ? '' : 's' }} available
      </span>
    </div>

    <!-- Wallet not connected: prompt -->
    <div v-if="!sourceWalletAddress" :class="promptPaddingClass + ' text-center'">
      <p class="text-sm text-[#4e7397]">
        Connect your {{ sourceChainType === 'solana' ? 'Solana' : 'EVM' }} wallet to select tokens
      </p>
    </div>

    <!-- Protected gateway is waiting for an authenticated TON session. -->
    <div
      v-else-if="tokenLoadBlockedMessage && availableSourceTokens.length === 0"
      :class="promptPaddingClass + ' text-center'"
    >
      <p class="text-sm text-[#4e7397]">{{ tokenLoadBlockedMessage }}</p>
    </div>

    <!-- Wallet connected but tokens loading -->
    <div
      v-else-if="isLoadingTokens && availableSourceTokens.length === 0"
      :class="promptPaddingClass + ' flex flex-col items-center gap-3'"
    >
      <svg class="w-6 h-6 text-[#1980e6] animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <p class="text-sm text-[#4e7397]">Loading your tokens...</p>
    </div>

    <!-- Wallet connected, tokens loaded -->
    <template v-else>
      <!-- Token Selector -->
      <div class="space-y-1.5">
        <label class="text-xs font-medium text-[#4e7397] uppercase tracking-wide">Token</label>
        <TokenSelector
          :tokens="availableSourceTokens"
          :selected-address="selectedSourceTokenAddress ?? undefined"
          :is-loading="isLoadingTokens"
          :has-connected-wallet="!!sourceWalletAddress"
          :chain-id="selectedChainId"
          :disabled="routeLocked"
          @select="$emit('select-token', $event)"
        />
      </div>

      <!-- Amount Input -->
      <div class="space-y-1.5">
        <div class="flex items-center justify-between">
          <label class="text-xs font-medium text-[#4e7397] uppercase tracking-wide">Amount</label>
          <button
            v-if="spendableBalanceLabel"
            type="button"
            class="text-xs text-[#1980e6] hover:text-[#4aa3ff] transition-colors disabled:cursor-not-allowed disabled:opacity-60 disabled:text-gray-400"
            title="Uses your full available balance. Network fees will be deducted from the transaction if needed."
            :disabled="routeLocked"
            @click="$emit('set-max')"
          >
            Max: {{ spendableBalanceLabel }} {{ balanceUsd }}
          </button>
        </div>
        <div class="relative">
          <input
            :value="sourceAmount"
            type="text"
            inputmode="decimal"
            placeholder="0.00"
            class="w-full rounded-xl border border-gray-200 px-4 py-3 text-lg text-[#0e141b] font-medium placeholder-gray-400 focus:outline-none focus:border-[#1980e6] focus:ring-2 focus:ring-[#1980e6]/20 transition-all disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-gray-100"
            :class="inputBgClass"
            :disabled="!selectedToken || routeLocked"
            @input="$emit('amount-input', $event)"
          />
          <div
            v-if="selectedToken"
            class="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#4e7397] font-medium"
          >
            {{ selectedToken.symbol }}
          </div>
        </div>
        <div v-if="quickAmountOptions.length > 0" class="flex flex-wrap gap-2">
          <button
            v-for="option in quickAmountOptions"
            :key="option.label"
            type="button"
            class="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-[#4e7397] transition-colors hover:border-[#1980e6]/40 hover:text-[#1980e6] disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-gray-100"
            :disabled="routeLocked"
            @click="$emit('quick-amount', option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <p v-if="sourceAmountUsd" class="text-xs text-[#4e7397]">≈ {{ sourceAmountUsd }}</p>
        <p v-if="amountValidationError" class="text-xs text-[#b42318]">
          {{ amountValidationError }}
        </p>
        <p v-else-if="recoveryHint" class="text-xs text-[#4e7397]">
          {{ recoveryHint }}
        </p>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import TokenSelector from './TokenSelector.vue'
import type { WalletSourceToken } from './contracts'

defineProps<{
  cardClass: string
  inputBgClass: string
  promptPaddingClass: string
  sourceChainType: 'evm' | 'solana'
  sourceWalletAddress: string | null | undefined
  availableSourceTokens: WalletSourceToken[]
  selectedSourceTokenAddress: string | null | undefined
  selectedChainId: number
  isLoadingTokens: boolean
  tokenLoadBlockedMessage: string | null
  tokensWithBalanceCount: number
  selectedToken: WalletSourceToken | undefined
  sourceAmount: string
  sourceAmountUsd: string | null
  balanceUsd: string | null
  spendableBalanceLabel: string | null
  quickAmountOptions: { label: string; value: string }[]
  amountValidationError: string | null
  recoveryHint: string | null
  routeLocked?: boolean
}>()

defineEmits<{
  (e: 'select-token', token: WalletSourceToken): void
  (e: 'amount-input', event: Event): void
  (e: 'set-max'): void
  (e: 'quick-amount', value: string): void
}>()
</script>
